/**
 * The eleven persona welcome tours (EPIC #1321, part 3/9).
 *
 * A short (5-7 step) opt-in orientation per landing/persona, phrased around the
 * user's workday -- not a feature catalogue. Each is registered in the central
 * registry and offered once, on first login, via the welcome prompt (D4-ONB-006);
 * an abandoned tour is resumable from Training (7/9).
 *
 * Copy rules (D4-ONB-011 / D4-WLB): agency-neutral via the ``{agency}`` token
 * (never a hardcoded agency/vendor name -- the D4-WLB-008 grep guard stays
 * green); plain professional language for non-technical realtors; NO emojis.
 * The paralegal / manager / accounts tours are VALUE-FREE -- they never name
 * commission or money, mirroring the view_commission masking those presets get.
 *
 * Gating (D4-ONB-004): steps declare the same permFlag / module gates the
 * sidebar's ``itemVisible`` uses, so a tour never points at a surface the user
 * cannot open. A step whose nav target is not on the page is silently advanced
 * past by the engine (TARGET_NOT_FOUND), so a narrative step uses ``center``.
 */
import type { TourDefinition, TourStep } from "./types";

/**
 * Shared "Capturing for agent" picker step (#2194, REQ D4-ONB-015).
 *
 * Capture-on-behalf (PRs #2183-2191, flag ``capture_on_behalf``) lets a
 * managerial persona -- paralegal, manager or super-admin -- load a deal for an
 * agent who is off sick or away and have it credited to that agent. This step is
 * the second half of that guided coverage: after beaconing the "Add a deal on
 * behalf" entry point on each persona's own surface, the tour walks the user to
 * the deal form and spotlights the "Capturing for agent" picker.
 *
 * Action/beacon (page=spotlight / action=beacon, #1341): the user is doing a
 * specific thing -- choosing the absent agent. Gated on ``capture_on_behalf`` so
 * a plain field agent (and the office-admin, who holds ``manage_users`` but NOT
 * ``capture_on_behalf``) never sees it. The route is the bare
 * ``/crm/deals/add-sale`` (the engine navigates a query-free path; the picker
 * renders for any capability holder regardless of sale/lease type, and the tour
 * only spotlights -- it never submits, so the sale Loom hard-gate is irrelevant).
 *
 * VALUE-FREE copy: the paralegal/manager welcome tours never name money, so the
 * body says "credited to them" -- never "commission". Spread into each tour so
 * its progress key stays per-tour while the definition lives in one place.
 */
const CAPTURE_ON_BEHALF_PICKER_STEP: TourStep = {
  id: "capture-on-behalf-picker",
  route: "/crm/deals/add-sale",
  target: "on-behalf-agent-picker",
  kind: "action",
  section: "Add a deal on behalf",
  permFlag: "capture_on_behalf",
  title: "Pick the agent you are capturing for",
  body: "On the deal form, the Capturing for agent picker is where you choose the agent who is away. Pick them and the deal is credited to them, not to you. Leave it on Myself to capture your own deal.",
  placement: "bottom",
};

/**
 * Shared "Your toolbar" welcome step (#2452). Spotlights the header action
 * cluster (Cmd/Ctrl-K search, notifications, feedback, replay-tour launcher)
 * that sits on every page in both shells. Appended as the FINAL step of every
 * welcome tour. A FACTORY (not a single shared object) because each welcome tour
 * lives on a different landing route/shell -- the step must stay on the tour's
 * own route so the engine never navigates the user off their shell. The
 * `header-toolbar` anchor is present on every CRM and admin page, so the target
 * always resolves. Value-free (no money words) so it is safe on the value-free
 * paralegal/manager/accounts spines.
 */
const makeToolbarStep = (route: string): TourStep => ({
  id: "your-toolbar",
  route,
  target: "header-toolbar",
  section: "Your toolbar",
  title: "Your toolbar, on every page",
  body: "Up here on every page you have quick search -- press Ctrl or Cmd plus K to jump to any record, page or action -- your notifications, a Feedback button to tell us about anything, and the tour button to replay a walkthrough of the page you are on. That is the tour; explore at your own pace.",
  placement: "bottom",
});

/**
 * Agent -- lands on /crm/dashboard.
 * Spine: your day -> your people -> your properties & sales -> your money -> help.
 */
export const AGENT_WELCOME_TOUR: TourDefinition = {
  id: "agent-welcome",
  title: "Welcome to {agency}",
  steps: [
    {
      id: "welcome",
      route: "/crm/dashboard",
      target: "center",
      section: "Welcome",
      title: "Welcome to {agency}",
      body: "Here is a quick two-minute tour of your workspace. You can leave any time with End tour or the Esc key, and start it again later from Training.",
      placement: "center",
    },
    {
      id: "your-day",
      route: "/crm/dashboard",
      target: "dashboard-home",
      section: "Your day",
      title: "Your day at a glance",
      body: "Your dashboard rolls up your own pipeline, targets and progress so you can see where things stand the moment you log in.",
      placement: "bottom",
    },
    {
      id: "your-people",
      route: "/crm/dashboard",
      target: "nav-/crm/contacts",
      section: "Your people",
      navGroup: "people",
      title: "Your people",
      body: "Your contacts, farm and buyers live under Prospecting. This is where you build and work your database.",
      placement: "right",
    },
    {
      id: "your-properties",
      route: "/crm/dashboard",
      target: "nav-/crm/listings",
      section: "Your properties",
      navGroup: "property-deals",
      title: "Your properties and sales",
      body: "Load your mandates as listings and track every sale through to registration under Property and Deals.",
      placement: "right",
    },
    {
      // Issue #1343 (bug 4): this step now LANDS the agent on their own My Stats
      // earnings surface (/crm/commission) and spotlights the page itself, rather
      // than staying on the dashboard and merely highlighting the sidebar link --
      // which read to the user as the tour "bouncing back to the dashboard". The
      // route resolves without a redirect for anyone with view_commission (a
      // standard field agent + Finance); CommissionRoute only blocks the
      // value-free back-office presets, who never see this step (it is gated on
      // view_commission, so the engine filters it out for them). The page anchor
      // is guaranteed present on /crm/commission (see MyCommission.tsx).
      id: "your-money",
      route: "/crm/commission",
      target: "page-my-commission",
      section: "Your money",
      module: "financial",
      permFlag: "view_commission",
      title: "Your earnings and targets",
      body: "This is My Stats -- your commission, targets and leaderboard standing, kept up to date as your deals progress. Your earnings, paid and outstanding, are always a click away here.",
      placement: "bottom",
    },
    {
      // Issue #1522: the help step now STAYS on /crm/commission for the common
      // (commission-visible) path -- the route the preceding `your-money` step
      // lands on -- so the tour no longer bounces the user back to /crm/dashboard
      // purely to spotlight the Training nav link (which is present in the sidebar
      // on every CRM page). Gated on view_commission + the financial module so it
      // pairs with `your-money`: a value-free agent (no view_commission) never
      // reaches /crm/commission (CommissionRoute would 403), so they instead get
      // the `help-basic` variant below on /crm/dashboard.
      id: "help",
      route: "/crm/commission",
      target: "nav-/crm/training",
      section: "Help",
      navGroup: "comms-docs",
      module: "financial",
      permFlag: "view_commission",
      title: "Help whenever you need it",
      body: "Training holds how-to guides and lets you replay this tour any time.",
      placement: "right",
    },
    {
      // Issue #1522: value-free fallback. A user WITHOUT view_commission never
      // saw the `your-money` step and is still on /crm/dashboard, so their help
      // step stays there (the commission `help` variant above is excluded for
      // them by its view_commission gate; this one is excluded for a
      // commission-visible user by excludeFlag, so exactly ONE help step shows).
      id: "help-basic",
      route: "/crm/dashboard",
      target: "nav-/crm/training",
      section: "Help",
      navGroup: "comms-docs",
      module: "training",
      excludeFlag: "view_commission",
      title: "Help whenever you need it",
      body: "Training holds how-to guides and lets you replay this tour any time.",
      placement: "right",
    },
    // #2452: the shared "Your toolbar" orientation step, appended INSIDE the agent
    // steps literal so CUSTOM_WELCOME_TOUR (which maps these steps) inherits it.
    // A final hop back to /crm/dashboard is fine -- same shell as both the
    // commission `help` (/crm/commission) and value-free `help-basic` variants.
    makeToolbarStep("/crm/dashboard"),
  ],
};

/**
 * Admin -- lands on /admin/dashboard. Offered AFTER the first-run Setup Wizard
 * (#516), never over it. Ends by pointing at the ViewSwitcher.
 */
export const ADMIN_WELCOME_TOUR: TourDefinition = {
  id: "admin-welcome",
  title: "Welcome to {agency}",
  steps: [
    {
      id: "welcome",
      route: "/admin/dashboard",
      target: "center",
      section: "Welcome",
      title: "Welcome to {agency}",
      body: "Here is a quick tour of your admin workspace. You can leave any time with End tour or the Esc key, and start it again later from Training.",
      placement: "center",
    },
    {
      id: "overview",
      route: "/admin/dashboard",
      target: "admin-dashboard-home",
      section: "Overview",
      title: "Your company overview",
      body: "The dashboard gives you a company-wide overview with quick links to every area of the business.",
      placement: "bottom",
    },
    {
      id: "dashboards",
      route: "/admin/dashboard",
      target: "nav-/admin/deal-pipeline",
      section: "Dashboards",
      navGroup: "dashboards",
      title: "Company dashboards",
      body: "Track the whole agency from here: pipeline, leaderboard, sales targets and market share, all company-wide.",
      placement: "right",
    },
    {
      id: "reports",
      route: "/admin/dashboard",
      target: "nav-/admin/kpis",
      section: "Reports",
      navGroup: "reports",
      title: "Reports",
      body: "Reports cover KPI performance, activity and adoption -- the numbers you take to a sales meeting.",
      placement: "right",
    },
    {
      id: "users",
      route: "/admin/dashboard",
      target: "nav-/admin/users",
      section: "User Management",
      navGroup: "dashboards",
      title: "Invite your agents",
      body: "User Management is where you invite your agents and office staff and manage their access. This is usually your first job.",
      placement: "right",
    },
    {
      id: "compliance",
      route: "/admin/dashboard",
      target: "nav-/admin/compliance",
      section: "Compliance",
      navGroup: "compliance",
      module: "fica",
      title: "Compliance",
      body: "Keep FICA compliance and document checks on track across every deal in the agency.",
      placement: "right",
    },
    {
      id: "view-switcher",
      route: "/admin/dashboard",
      target: "view-switcher",
      section: "Switch view",
      title: "See what an agent sees",
      body: "Use the view switch up here to step into the agent CRM and see exactly what your agents do.",
      placement: "bottom",
    },
    makeToolbarStep("/admin/dashboard"),
  ],
};

/**
 * Paralegal -- lands on /paralegal. VALUE-FREE: never names commission or money.
 * Spine: deals to process -> legal queue -> behind schedule -> templates ->
 * conveyancers -> user management (scoped) -> tasks/training.
 */
export const PARALEGAL_WELCOME_TOUR: TourDefinition = {
  id: "paralegal-welcome",
  title: "Welcome to {agency}",
  steps: [
    {
      id: "welcome",
      route: "/paralegal",
      target: "center",
      section: "Welcome",
      title: "Welcome to {agency}",
      body: "Here is a quick tour of your workspace. You can leave any time with End tour or the Esc key, and start it again later from Training.",
      placement: "center",
    },
    {
      id: "deals-to-process",
      route: "/paralegal",
      target: "paralegal-queue",
      section: "Written Deals",
      title: "Written Deals",
      body: "This is your work queue: every deal that needs paperwork prepared, sorted so the most pressing files come first.",
      placement: "bottom",
    },
    {
      id: "legal-queue",
      route: "/paralegal",
      target: "nav-/legal",
      section: "Deals in Process",
      navGroup: "legal-finance",
      title: "Deals in Process",
      body: "The same deal files as Written Deals, in one list ranked by deadline risk. Use it to pick the riskiest file next, then open it to work its cover-page milestones.",
      placement: "right",
    },
    {
      id: "behind-schedule",
      route: "/paralegal",
      target: "nav-/behind-schedule",
      section: "Behind schedule",
      navGroup: "legal-finance",
      title: "Behind schedule",
      body: "Behind Schedule flags the files that have slipped past their milestones so nothing quietly stalls.",
      placement: "right",
    },
    {
      id: "templates",
      route: "/paralegal",
      target: "nav-/crm/documents",
      section: "Templates",
      navGroup: "comms-docs",
      module: "documents",
      permFlag: "capture_deal",
      title: "Templates",
      body: "Prepare letters and documents from the approved templates -- mandates, disclosures and the rest.",
      placement: "right",
    },
    {
      id: "conveyancers",
      route: "/paralegal",
      target: "nav-/crm/conveyancers",
      section: "Conveyancers",
      navGroup: "property-deals",
      permFlag: "capture_deal",
      title: "Conveyancers",
      body: "The conveyancer directory keeps the attorneys you work with, and who is handling which transfer, in one place.",
      placement: "right",
    },
    {
      // Scoped User Management: the paralegal (rank 4) may onboard and manage
      // users of strictly lower rank. Gated like the nav item (manage_users).
      id: "users",
      route: "/paralegal",
      target: "nav-/crm/users",
      section: "User Management",
      navGroup: "settings",
      permFlag: "manage_users",
      title: "Your team",
      body: "User Management is where you invite new staff and agents and keep their access in order, for the people below you in the office hierarchy.",
      placement: "right",
    },
    {
      // Add a deal on behalf (#2194, REQ D4-ONB-015). Beacons the "Add a deal on
      // behalf" header button -- the paralegal's entry point for loading a deal
      // for an agent who is away. Gated on capture_on_behalf, so it degrades away
      // for anyone without the capability. VALUE-FREE copy (no money words).
      id: "capture-on-behalf",
      route: "/paralegal",
      target: "paralegal-capture-on-behalf",
      kind: "action",
      section: "Add a deal on behalf",
      permFlag: "capture_on_behalf",
      title: "Capture for an agent who is away",
      body: "When an agent is off sick or away, you can load a deal or a new listing on their behalf from here. It is captured under their name and credited to them, not to you.",
      placement: "bottom",
    },
    { ...CAPTURE_ON_BEHALF_PICKER_STEP },
    {
      id: "help",
      route: "/paralegal",
      target: "nav-/crm/training",
      section: "Help",
      navGroup: "comms-docs",
      module: "training",
      title: "Tasks and help",
      body: "Tasks keep your follow-ups in order, and Training holds the how-to guides and this tour.",
      placement: "right",
    },
    makeToolbarStep("/paralegal"),
  ],
};

/**
 * Finance -- lands on /finance. (Finance MAY see commission, so this tour can
 * speak in money terms where useful, but stays workday-framed.)
 * Spine: finance queue -> authorizations -> behind schedule -> conveyancers
 * -> FICA outstanding / deals lodged -> tasks/training. (D4-FBK-4405: never a
 * Listings / Templates / Vault / Legal Queue step -- the finance desk's nav
 * does not carry them.)
 */
export const FINANCE_WELCOME_TOUR: TourDefinition = {
  id: "finance-welcome",
  title: "Welcome to {agency}",
  steps: [
    {
      id: "welcome",
      route: "/finance",
      target: "center",
      section: "Welcome",
      title: "Welcome to {agency}",
      body: "Here is a quick tour of your workspace. You can leave any time with End tour or the Esc key, and start it again later from Training.",
      placement: "center",
    },
    {
      id: "finance-queue",
      route: "/finance",
      target: "finance-queue",
      section: "Finance queue",
      title: "Your finance queue",
      body: "This is your work queue. The tiles at the top count what is waiting (deposits to invest and withdraw, lodged and registered deals, commission authorizations) and each opens its list; the tabs below hold the deals waiting on finance, ordered so the items that need attention surface first.",
      placement: "bottom",
    },
    {
      // Issue #1522: previously a `center`/nav narrative that named the
      // authorizations inbox without ever showing it -- and pointed at the SAME
      // Finance Queue nav link the `finance-queue` step already covers. It now
      // LANDS the user on the real authorizations surface (/finance/authorizations,
      // the CommissionAuthorizationInbox the Finance preset reaches via
      // process_payment) and spotlights the page itself, so the two steps describe
      // two different screens. Page anchor present on CommissionAuthorizationInbox.
      id: "authorizations",
      route: "/finance/authorizations",
      target: "finance-authorizations",
      section: "Authorizations",
      module: "financial",
      permFlag: "process_payment",
      title: "Your authorizations inbox",
      body: "This is where you carry out the first finance approval that releases a deal's commission, before it moves on for the final sign-off.",
      placement: "bottom",
    },
    {
      id: "behind-schedule",
      route: "/finance",
      target: "nav-/behind-schedule",
      section: "Behind schedule",
      navGroup: "legal-finance",
      title: "Behind schedule",
      body: "Behind Schedule flags the deals that have slipped past their milestones so nothing stalls unnoticed.",
      placement: "right",
    },
    {
      id: "conveyancers",
      route: "/finance",
      target: "nav-/crm/conveyancers",
      section: "Conveyancers",
      navGroup: "property-deals",
      permFlag: "capture_deal",
      title: "Conveyancers",
      body: "The conveyancer directory keeps the attorneys you work with, and who is handling which transfer, in one place.",
      placement: "right",
    },
    {
      // FICA Outstanding (#2452, #2265). Company-wide payment-readiness view --
      // every deal whose required FICA is still outstanding is flagged "not ready
      // for payment". Gated on view_all_fica so it degrades for a finance user
      // without company-wide FICA visibility.
      id: "fica-outstanding",
      route: "/finance",
      target: "nav-/crm/finance/fica-outstanding",
      section: "FICA Outstanding",
      navGroup: "legal-finance",
      module: "fica",
      permFlag: "view_all_fica",
      title: "See what FICA is outstanding",
      body: "FICA Outstanding flags every deal whose required FICA is still outstanding, so you can see at a glance what is not yet ready for payment.",
      placement: "right",
    },
    {
      // Deals Lodged (#2452, D5-ASW-025). Company-wide read-only view of open
      // sales awaiting registration (not yet lodged / Lodged) -- a cash-flow
      // sight-line. Gated on view_all_fica.
      id: "deals-lodged",
      route: "/finance",
      target: "nav-/crm/finance/deals-lodged",
      section: "Deals Lodged",
      navGroup: "legal-finance",
      module: "financial",
      permFlag: "view_all_fica",
      title: "The deals waiting to register",
      body: "Deals Lodged is the company-wide, read-only view of every open sale waiting to register, each marked Offer received, At attorney or Lodged, with a Not yet lodged / Lodged / All filter, so you can see what commission is coming down the line.",
      placement: "right",
    },
    {
      id: "help",
      route: "/finance",
      target: "nav-/crm/training",
      section: "Help",
      navGroup: "comms-docs",
      module: "training",
      title: "Tasks and help",
      body: "Tasks keep your follow-ups in order, and Training holds the how-to guides and this tour.",
      placement: "right",
    },
    makeToolbarStep("/finance"),
  ],
};

/**
 * Manager -- lands on /management. VALUE-FREE: no money figures.
 * Spine: deal sign-off -> Oversight (the read-only company dashboards) -> the
 * master Deal Queue -> behind schedule -> user management (scoped) ->
 * tasks/training.
 *
 * Oversight (#1411, EPIC #1410, folded in here by #1521): a manager
 * (view_management_dashboards) oversees the business READ-ONLY through the six
 * dashboards in the crmNav ``oversight`` group -- Monthly Summary, Deal
 * Pipeline, Attorney Pipeline, FICA, KPI Reports and Communications. One grouped
 * step spotlights that nav group (gated exactly like the group:
 * ``view_management_dashboards``) and names the six surfaces WITHOUT naming any
 * money -- commission stays masked for the manager, so the copy stays value-free.
 * (The previous separate back-office manager DESK tour was removed in #1521; its
 * coverage lives here in the one canonical welcome journey.)
 */
export const MANAGER_WELCOME_TOUR: TourDefinition = {
  id: "manager-welcome",
  title: "Welcome to {agency}",
  steps: [
    {
      id: "welcome",
      route: "/management",
      target: "center",
      section: "Welcome",
      title: "Welcome to {agency}",
      body: "Here is a quick tour of your workspace. You can leave any time with End tour or the Esc key, and start it again later from Training.",
      placement: "center",
    },
    {
      id: "signoff-queue",
      route: "/management",
      target: "signoff-queue",
      section: "Sign-off queue",
      title: "Deals to sign off",
      body: "This is your sign-off queue: the deals waiting on your approval before they proceed.",
      placement: "bottom",
    },
    {
      // Oversight (#1411, folded into this welcome journey by #1521). One grouped
      // step that spotlights the read-only company dashboards in the sidebar's
      // Oversight group. Gated on view_management_dashboards exactly like the
      // group, so it vanishes for anyone without the management flag. VALUE-FREE:
      // it names the six oversight surfaces but never commission or rand figures
      // (the manager's view keeps commission masked).
      id: "oversight",
      route: "/management",
      target: "nav-/crm/oversight/monthly-deals",
      section: "Oversight",
      navGroup: "oversight",
      permFlag: "view_management_dashboards",
      title: "Oversee the business",
      body: "Oversight gives you a read-only view right across the company -- the Monthly Summary, the Deal and Attorney pipelines, FICA, KPI reports and Communications are all here. Everything here is view-only, so you can see how the business is tracking and step in where you are needed.",
      placement: "right",
    },
    {
      // Master Deal Queue (#2823): the ONE filterable view of the outstanding
      // deal book. Lands on the page and spotlights it; gated like its nav item
      // (view_management_dashboards). VALUE-FREE.
      id: "deal-queue",
      route: "/crm/oversight/deal-queue",
      target: "page-oversight-deal-queue",
      section: "Deal Queue",
      permFlag: "view_management_dashboards",
      title: "Every deal still in process",
      body: "The Deal Queue is one list of every deal still in process across the company. Filter it by what is outstanding, by agent or by sale date to see exactly where files are waiting.",
      placement: "bottom",
    },
    {
      id: "behind-schedule",
      route: "/management",
      target: "nav-/behind-schedule",
      section: "Behind schedule",
      navGroup: "legal-finance",
      title: "Behind schedule",
      body: "Behind Schedule shows where deals have slipped past their milestones, so you can step in early.",
      placement: "right",
    },
    {
      id: "users",
      route: "/management",
      target: "nav-/crm/users",
      section: "User Management",
      navGroup: "settings",
      permFlag: "manage_users",
      title: "Your team",
      body: "User Management lets you manage the agents and staff that report to you and keep their access in order.",
      placement: "right",
    },
    {
      // Add a deal on behalf (#2194, REQ D4-ONB-015). Beacons the "Add a deal on
      // behalf" header button. A manager can also reach this from a specific
      // agent's row in User Management. Gated on capture_on_behalf; VALUE-FREE.
      id: "capture-on-behalf",
      route: "/management",
      target: "manager-capture-on-behalf",
      kind: "action",
      section: "Add a deal on behalf",
      permFlag: "capture_on_behalf",
      title: "Capture for an agent who is away",
      body: "When an agent is off sick or away, you can load a deal or a new listing on their behalf from here. It is captured under their name and credited to them, not to you. You can also start this from a specific agent's row in User Management.",
      placement: "bottom",
    },
    { ...CAPTURE_ON_BEHALF_PICKER_STEP },
    {
      id: "help",
      route: "/management",
      target: "nav-/crm/training",
      section: "Help",
      navGroup: "comms-docs",
      module: "training",
      title: "Tasks and help",
      body: "Tasks keep your follow-ups in order, and Training holds the how-to guides and this tour.",
      placement: "right",
    },
    makeToolbarStep("/management"),
  ],
};

/**
 * Accounts -- lands on /accounts. VALUE-FREE: never names commission.
 * Spine: deposits to invest -> conveyancers -> tasks/training.
 */
export const ACCOUNTS_WELCOME_TOUR: TourDefinition = {
  id: "accounts-welcome",
  title: "Welcome to {agency}",
  steps: [
    {
      id: "welcome",
      route: "/accounts",
      target: "center",
      section: "Welcome",
      title: "Welcome to {agency}",
      body: "Here is a quick tour of your workspace. You can leave any time with End tour or the Esc key, and start it again later from Training.",
      placement: "center",
    },
    {
      id: "deposits-to-invest",
      route: "/accounts",
      target: "accounts-queue",
      section: "Deposits to invest",
      title: "Deposits to invest",
      body: "This is your work queue: the deposits waiting to be placed on investment, ordered so the items that need action come first.",
      placement: "bottom",
    },
    {
      id: "conveyancers",
      route: "/accounts",
      target: "nav-/crm/conveyancers",
      section: "Conveyancers",
      navGroup: "property-deals",
      permFlag: "capture_deal",
      title: "Conveyancers",
      body: "The conveyancer directory keeps the attorneys you work with, and who is handling which transfer, in one place.",
      placement: "right",
    },
    {
      id: "tasks",
      route: "/accounts",
      target: "nav-/crm/tasks",
      section: "Tasks",
      navGroup: "workspace",
      title: "Your tasks",
      body: "Tasks keep your follow-ups in order so nothing waiting on you slips through.",
      placement: "right",
    },
    {
      id: "help",
      route: "/accounts",
      target: "nav-/crm/training",
      section: "Help",
      navGroup: "comms-docs",
      module: "training",
      title: "Help whenever you need it",
      body: "Training holds the how-to guides and lets you replay this tour any time.",
      placement: "right",
    },
    makeToolbarStep("/accounts"),
  ],
};

/**
 * Office Admin -- lands on /crm/dashboard. Their distinguishing job is listing
 * administration (#2847: user management was withdrawn from this seat per
 * Natalie's 8-rank hierarchy — rank 6 holds no ``manage_users``, #2840, so the
 * old "Manage your people" step and the /crm/users landing are gone). No money
 * (no view_commission); they share the agent-book surfaces.
 * Spine: dashboard -> shared surfaces -> list a property -> training.
 *
 * List a property (#1412, added by #1521): the office-admin holds the narrow
 * ``list_properties`` flag (NOT capture_deal) and so may LIST a property from
 * the Listings page. One step spotlights Listings, gated on ``list_properties``
 * exactly like the sidebar nav item's ``listingCapable`` rule, so it shows only
 * for a listing-capable office admin.
 */
export const OFFICE_ADMIN_WELCOME_TOUR: TourDefinition = {
  id: "office-admin-welcome",
  title: "Welcome to {agency}",
  steps: [
    {
      id: "welcome",
      route: "/crm/dashboard",
      target: "center",
      section: "Welcome",
      title: "Welcome to {agency}",
      body: "Here is a quick tour of your workspace. You can leave any time with End tour or the Esc key, and start it again later from Training.",
      placement: "center",
    },
    // NB there is deliberately NO "Prospecting" step here (issue #1959 QC fix):
    // the office_admin is a BACK-OFFICE preset, so the whole Prospecting group
    // (Contacts / Farm / Referrals / Buyers -- all ``agentBookOnly``) is hidden
    // from its sidebar (crmNav.ts drops agentBookOnly items for the back-office
    // presets). Spotlighting ``nav-/crm/contacts`` therefore anchored to an
    // element that never renders for this persona -- the step showed no tooltip
    // and the engine STALLED ~8s on the missing target before skipping it. The
    // office-admin's real job (User Management + listing a property) is covered
    // by the ``users`` and ``list-property`` steps.
    {
      // List a property (#1412, added by #1521). The office-admin holds the
      // narrow list_properties flag (NOT capture_deal) and may list a property
      // from the Listings page. Gated on list_properties exactly like the
      // sidebar Listings item's listingCapable rule, so it only shows for a
      // listing-capable office admin.
      id: "list-property",
      route: "/crm/dashboard",
      target: "nav-/crm/listings",
      section: "Listings",
      navGroup: "property-deals",
      permFlag: "list_properties",
      title: "List a property",
      body: "Listings is where you add and manage the agency's properties. As an office administrator you can list a property here and keep its details up to date.",
      placement: "right",
    },
    {
      id: "help",
      route: "/crm/dashboard",
      target: "nav-/crm/training",
      section: "Help",
      navGroup: "comms-docs",
      module: "training",
      title: "Help whenever you need it",
      body: "Training holds how-to guides and lets you replay this tour any time.",
      placement: "right",
    },
    makeToolbarStep("/crm/dashboard"),
  ],
};

/**
 * HR / agent-compliance manager (#2452, EPIC #2033) -- lands on
 * /crm/compliance/agents (the HR landing, #2454). VALUE-FREE: HR never sees
 * money, so the copy names no commission/earnings/payout. Their whole job is the
 * agent-compliance roster -- every agent's contracts / FFC / PPRA registration /
 * qualifications, with a green/amber/red status -- which they record and verify.
 *
 * Audience-gated to the ``hr`` preset (#2452): only the HR persona is ever
 * OFFERED this tour, so a manager who lands on the same roster route via
 * read-only oversight is never prompted with it. Per-step gating still mirrors
 * CrmSidebar.itemVisible (the record-and-verify action step gates on
 * ``manage_compliance``).
 *
 * Spine: welcome -> the compliance roster -> the status summary -> record &
 * verify -> user management (scoped) -> help.
 */
export const HR_WELCOME_TOUR: TourDefinition = {
  id: "hr-welcome",
  title: "Welcome to {agency}",
  // D5-RBP-005 (#4175): the combined HR/Paralegal desk also owns the roster,
  // so the launcher still offers her this tour on /crm/compliance/agents.
  audience: { preset: ["hr", "hr_paralegal"] },
  steps: [
    {
      id: "welcome",
      route: "/crm/compliance/agents",
      target: "center",
      section: "Welcome",
      title: "Welcome to {agency}",
      body: "Here is a quick tour of your workspace. You can leave any time with End tour or the Esc key, and start it again later from Training.",
      placement: "center",
    },
    {
      id: "roster",
      route: "/crm/compliance/agents",
      target: "page-admin-compliance-agents",
      section: "Agent Compliance",
      title: "Your compliance roster",
      body: "This is the agent-compliance roster -- every agent's contracts, FFC, PPRA registration and qualifications, each with a green, amber or red status from the recorded dates. It replaces the spreadsheet.",
      placement: "bottom",
    },
    {
      id: "at-a-glance",
      route: "/crm/compliance/agents",
      target: "compliance-summary",
      section: "At a glance",
      title: "The status at a glance",
      body: "These tiles count how many documents are valid, expiring soon, expired or missing across the agency, so you can see where to act first.",
      placement: "bottom",
    },
    {
      // ACTION step (#1342): beacon on the roster so the user learns where to
      // open an agent and record a document. Gated on manage_compliance -- the
      // same capability the write endpoints require -- so a read-only overseer
      // never sees it. No openVia: the roster is always on the page.
      id: "record-verify",
      route: "/crm/compliance/agents",
      target: "compliance-roster",
      kind: "action",
      section: "Record and verify",
      permFlag: "manage_compliance",
      title: "Record and verify a document",
      body: "Open an agent's row to record a document, set a reason code where something is outstanding, and verify it once it is in order. Give the beacon a click to see where to start.",
      placement: "bottom",
    },
    {
      // Scoped User Management: HR (rank 5) may onboard and manage users of
      // strictly lower rank. Gated like the nav item (manage_users).
      id: "users",
      route: "/crm/compliance/agents",
      target: "nav-/crm/users",
      section: "User Management",
      navGroup: "settings",
      permFlag: "manage_users",
      title: "Onboard your people",
      body: "User Management is where you invite new agents and staff and keep their access in order, for the people below you in the office hierarchy.",
      placement: "right",
    },
    {
      id: "help",
      route: "/crm/compliance/agents",
      target: "nav-/crm/training",
      section: "Help",
      navGroup: "comms-docs",
      module: "training",
      title: "Help whenever you need it",
      body: "Training holds the how-to guides and lets you replay this tour any time.",
      placement: "right",
    },
    makeToolbarStep("/crm/compliance/agents"),
  ],
};

/**
 * HR/Paralegal -- the combined desk (D5-RBP-005, #4175): one person who works
 * the paralegal desk AND owns the agent-compliance roster. Lands on /paralegal
 * (``landingPathForUser``), so every step stays there; the HR half is
 * introduced through its nav item, and the full HR roster tour (``hr-welcome``,
 * audience-widened to this preset) is offered on the roster page itself.
 * VALUE-FREE: neither desk sees money, so the copy names none.
 *
 * Spine: deals to process -> legal queue -> behind schedule -> agent
 * compliance -> user management (scoped) -> capture on behalf -> help.
 */
export const HR_PARALEGAL_WELCOME_TOUR: TourDefinition = {
  id: "hr-paralegal-welcome",
  title: "Welcome to {agency}",
  audience: { preset: "hr_paralegal" },
  steps: [
    {
      id: "welcome",
      route: "/paralegal",
      target: "center",
      section: "Welcome",
      title: "Welcome to {agency}",
      body: "Your login covers two desks: the deal files you process and the agent-compliance roster. Here is a quick tour. You can leave any time with End tour or the Esc key, and start it again later from Training.",
      placement: "center",
    },
    {
      id: "deals-to-process",
      route: "/paralegal",
      target: "paralegal-queue",
      section: "Written Deals",
      title: "Written Deals",
      body: "This is your deal work queue: every deal that needs paperwork prepared, sorted so the most pressing files come first.",
      placement: "bottom",
    },
    {
      id: "legal-queue",
      route: "/paralegal",
      target: "nav-/legal",
      section: "Deals in Process",
      navGroup: "legal-finance",
      title: "Deals in Process",
      body: "The same deal files as Written Deals, in one list ranked by deadline risk. Use it to pick the riskiest file next, then open it to work its cover-page milestones.",
      placement: "right",
    },
    {
      id: "behind-schedule",
      route: "/paralegal",
      target: "nav-/behind-schedule",
      section: "Behind schedule",
      navGroup: "legal-finance",
      title: "Behind schedule",
      body: "Behind Schedule flags the files that have slipped past their milestones so nothing quietly stalls.",
      placement: "right",
    },
    {
      // The HR half: the agent-compliance roster, gated like its nav item.
      id: "agent-compliance",
      route: "/paralegal",
      target: "nav-/crm/compliance/agents",
      section: "Agent Compliance",
      navGroup: "settings",
      permFlag: "manage_compliance",
      title: "Your compliance roster",
      body: "Agent Compliance is your HR desk: every agent's contracts, FFC, PPRA registration and qualifications, with a green, amber or red status. Open it to record and verify documents; it has its own short tour.",
      placement: "right",
    },
    {
      // Scoped User Management: the combined desk ranks with the paralegal
      // (rank 4) and may manage users of strictly lower rank only.
      id: "users",
      route: "/paralegal",
      target: "nav-/crm/users",
      section: "User Management",
      navGroup: "settings",
      permFlag: "manage_users",
      title: "Your team",
      body: "User Management is where you invite new staff and agents and keep their access in order, for the people below you in the office hierarchy.",
      placement: "right",
    },
    {
      id: "capture-on-behalf",
      route: "/paralegal",
      target: "paralegal-capture-on-behalf",
      kind: "action",
      section: "Add a deal on behalf",
      permFlag: "capture_on_behalf",
      title: "Capture for an agent who is away",
      body: "When an agent is off sick or away, you can load a deal or a new listing on their behalf from here. It is captured under their name and credited to them, not to you.",
      placement: "bottom",
    },
    { ...CAPTURE_ON_BEHALF_PICKER_STEP },
    {
      id: "help",
      route: "/paralegal",
      target: "nav-/crm/training",
      section: "Help",
      navGroup: "comms-docs",
      module: "training",
      title: "Tasks and help",
      body: "Tasks keep your follow-ups in order, and Training holds the how-to guides and this tour.",
      placement: "right",
    },
    makeToolbarStep("/paralegal"),
  ],
};

/**
 * Canvasser / sub-agent (#2467, A5 EPIC #2287) -- lands on /crm/farm (the
 * canvasser landing, ProtectedRoute.landingPathForUser). VALUE-FREE: a canvasser
 * never sees money, so the copy names no commission/earnings/payout. Their whole
 * job is working the farmbook and capturing the people they meet -- they may add
 * a phone number to a contact, but have NO listings / deals / buyers / money /
 * management.
 *
 * Audience-gated to the ``canvasser`` preset (#2467): only the canvasser persona
 * is ever OFFERED this tour, so a plain field agent who lands on /crm/farm via
 * the header launcher is never prompted with it (the same mechanism HR uses on
 * /crm/compliance/agents). Every step routes to /crm/farm so the tour starts on
 * the canvasser landing and never bounces to another page. Per-step gating still
 * mirrors CrmSidebar.itemVisible (the Contacts step gates on
 * ``add_contact_numbers``).
 *
 * Spine: welcome -> the farmbook -> canvassing -> property lookup -> capture
 * contacts -> your agenda -> tasks -> help.
 */
export const CANVASSER_WELCOME_TOUR: TourDefinition = {
  id: "canvasser-welcome",
  title: "Welcome to {agency}",
  audience: { preset: "canvasser" },
  steps: [
    {
      id: "welcome",
      route: "/crm/farm",
      target: "center",
      section: "Welcome",
      title: "Welcome to {agency}",
      body: "Here is a quick tour of your workspace. You can leave any time with End tour or the Esc key, and start it again later from Training.",
      placement: "center",
    },
    {
      id: "your-farm",
      route: "/crm/farm",
      target: "nav-/crm/farm",
      section: "Farm",
      navGroup: "people",
      module: "farm",
      title: "Your farmbook",
      body: "The farmbook is where you work the areas you canvass and log what you find as you go.",
      placement: "right",
    },
    {
      id: "canvassing",
      route: "/crm/farm",
      target: "nav-/crm/canvassing",
      section: "Canvassing",
      navGroup: "people",
      module: "farm",
      title: "Canvassing",
      body: "Canvassing prices the owner contact list for a street or area in your farm. LOOM quotes the cost before anything is spent. Bulk lists are ordered by the office; to reach one owner, use Property lookup.",
      placement: "right",
    },
    {
      id: "property-lookup",
      route: "/crm/farm",
      target: "nav-/crm/property-lookup",
      section: "Property lookup",
      navGroup: "people",
      module: "farm",
      title: "Property lookup",
      body: "Property lookup finds a property and shows its registered owner for free. A new deeds report or an owner's contact details shows its cost and asks you to confirm first; contact details come from your own monthly allowance.",
      placement: "right",
    },
    {
      id: "your-contacts",
      route: "/crm/farm",
      target: "nav-/crm/contacts",
      section: "Contacts",
      navGroup: "people",
      permFlag: "add_contact_numbers",
      title: "Capture the people you meet",
      body: "Contacts is where the people you meet are kept. You can add a phone number to a contact so the agent can follow up.",
      placement: "right",
    },
    {
      id: "your-agenda",
      route: "/crm/farm",
      target: "nav-/crm/appointments",
      section: "Agenda",
      navGroup: "workspace",
      title: "Your agenda",
      body: "Your calendar keeps your canvassing rounds and reminders in order.",
      placement: "right",
    },
    {
      id: "tasks",
      route: "/crm/farm",
      target: "nav-/crm/tasks",
      section: "Tasks",
      navGroup: "workspace",
      title: "Your tasks",
      body: "Tasks keep your follow-ups so nothing slips.",
      placement: "right",
    },
    {
      id: "help",
      route: "/crm/farm",
      target: "nav-/crm/training",
      section: "Help",
      navGroup: "comms-docs",
      module: "training",
      title: "Help whenever you need it",
      body: "Training holds the how-to guides and lets you replay this tour any time.",
      placement: "right",
    },
    makeToolbarStep("/crm/farm"),
  ],
};

/**
 * Combined Finance/Accounts (#1506, #1520) -- lands on /finance-accounts, the
 * single workspace that surfaces BOTH the commission-release (Finance) and the
 * deposit-to-invest (Accounts) functions for the ONE person at LWP who does
 * both. This persona holds view_commission, so -- unlike the value-free
 * paralegal/manager/accounts spines -- money language is allowed.
 *
 * The journey CENTRES on /finance-accounts and never bounces the user to a
 * half-surface: every step spotlights the on-page ``finance-accounts-queue``
 * anchor (the workspace's two tabs -- Finance and Accounts -- live there).
 * #1591 removed the separate "Finance Queue" /finance and "Deposits to Invest"
 * /accounts sidebar links (the combined Finance/Accounts link is the single
 * money-queue entry now), so the two deep steps describe the on-page TABS
 * rather than highlighting nav links that no longer exist. Each deep step still
 * gates by capability (module ``financial`` + ``process_payment`` for the
 * Finance half, ``invest_funds`` for the Accounts half), so the tour degrades
 * gracefully for a finance-only or accounts-only user who reaches the surface.
 *
 * Spine: welcome -> your workspace -> the commission-release (Finance) half ->
 * the deposit-to-invest (Accounts) half -> behind schedule -> help.
 */
export const FINANCE_ACCOUNTS_WELCOME_TOUR: TourDefinition = {
  id: "finance-accounts-welcome",
  title: "Welcome to {agency}",
  steps: [
    {
      id: "welcome",
      route: "/finance-accounts",
      target: "center",
      section: "Welcome",
      title: "Welcome to {agency}",
      body: "Here is a quick tour of your workspace. You can leave any time with End tour or the Esc key, and start it again later from Training.",
      placement: "center",
    },
    {
      id: "your-workspace",
      route: "/finance-accounts",
      target: "finance-accounts-queue",
      section: "Your workspace",
      title: "Your Finance and Accounts workspace",
      body: "This one workspace brings both sides of your job together: releasing commission on the Finance tab and placing deposits on investment on the Accounts tab. The tiles at the top count what is waiting and each opens its list; switch between the two sides with the tabs below them.",
      placement: "bottom",
    },
    {
      id: "finance-half",
      route: "/finance-accounts",
      target: "finance-accounts-queue",
      section: "Commission release",
      module: "financial",
      permFlag: "process_payment",
      title: "Releasing commission",
      body: "The Finance tab is where you carry out the finance approval that releases commission on a deal -- authorized splits, commission runs and deposit interest, all in one place.",
      placement: "bottom",
    },
    {
      id: "accounts-half",
      route: "/finance-accounts",
      target: "finance-accounts-queue",
      section: "Deposits to invest",
      module: "financial",
      permFlag: "invest_funds",
      title: "Placing deposits on investment",
      body: "The Accounts tab lists the deposits waiting to be placed on investment, ordered so the items that need action come first. Switch to it with the tabs at the top of this workspace.",
      placement: "bottom",
    },
    {
      id: "behind-schedule",
      route: "/finance-accounts",
      target: "nav-/behind-schedule",
      section: "Behind schedule",
      navGroup: "legal-finance",
      module: "financial",
      anyFlag: ["process_payment", "invest_funds"],
      title: "Behind schedule",
      body: "Behind Schedule flags the deals that have slipped past their milestones so nothing stalls unnoticed.",
      placement: "right",
    },
    {
      // FICA Outstanding (#2452, #2265). Company-wide payment-readiness view,
      // gated on view_all_fica so it degrades for a user without company-wide
      // FICA visibility.
      id: "fica-outstanding",
      route: "/finance-accounts",
      target: "nav-/crm/finance/fica-outstanding",
      section: "FICA Outstanding",
      navGroup: "legal-finance",
      module: "fica",
      permFlag: "view_all_fica",
      title: "See what FICA is outstanding",
      body: "FICA Outstanding flags every deal whose required FICA is still outstanding, so you can see at a glance what is not yet ready for payment.",
      placement: "right",
    },
    {
      // Deals Lodged (#2452, D5-ASW-025). Company-wide read-only view of open
      // sales awaiting registration (not yet lodged / Lodged) -- a cash-flow
      // sight-line. Gated on view_all_fica.
      id: "deals-lodged",
      route: "/finance-accounts",
      target: "nav-/crm/finance/deals-lodged",
      section: "Deals Lodged",
      navGroup: "legal-finance",
      module: "financial",
      permFlag: "view_all_fica",
      title: "The deals waiting to register",
      body: "Deals Lodged is the company-wide, read-only view of every open sale waiting to register, each marked Offer received, At attorney or Lodged, with a Not yet lodged / Lodged / All filter, so you can see what commission is coming down the line.",
      placement: "right",
    },
    {
      id: "help",
      route: "/finance-accounts",
      target: "nav-/crm/training",
      section: "Help",
      navGroup: "comms-docs",
      module: "training",
      title: "Tasks and help",
      body: "Tasks keep your follow-ups in order, and Training holds the how-to guides and this tour.",
      placement: "right",
    },
    makeToolbarStep("/finance-accounts"),
  ],
};

/**
 * Custom preset -- no bespoke journey. The agent spine, auto-filtered by the
 * user's permission flags / modules (D4-ONB-004), so a hybrid preset sees only
 * the surfaces it can actually open. Re-uses the AGENT spine steps under a
 * custom id so its progress is tracked separately.
 */
export const CUSTOM_WELCOME_TOUR: TourDefinition = {
  id: "custom-welcome",
  title: "Welcome to {agency}",
  steps: AGENT_WELCOME_TOUR.steps.map((s) => ({ ...s })),
};

/** Every persona welcome tour, in journey-table order. */
export const WELCOME_TOURS: TourDefinition[] = [
  AGENT_WELCOME_TOUR,
  ADMIN_WELCOME_TOUR,
  PARALEGAL_WELCOME_TOUR,
  FINANCE_WELCOME_TOUR,
  MANAGER_WELCOME_TOUR,
  ACCOUNTS_WELCOME_TOUR,
  OFFICE_ADMIN_WELCOME_TOUR,
  HR_WELCOME_TOUR,
  CANVASSER_WELCOME_TOUR,
  FINANCE_ACCOUNTS_WELCOME_TOUR,
  HR_PARALEGAL_WELCOME_TOUR,
  CUSTOM_WELCOME_TOUR,
];
